import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ConnectButton, useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccount, useConnect, useDisconnect, useReconnect, useSendTransaction, useWriteContract, useWaitForTransactionReceipt, useSwitchChain } from 'wagmi';
import { base, mainnet } from 'wagmi/chains';
import { parseEther, parseUnits, erc20Abi } from 'viem';
import { Robot, ShoppingCart, Lightning, Shield, Coins, Package, Plus, Minus, X, CaretLeft, CaretRight, Clock, Trash, Cube, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SupplierTrustBadge, WalletReputationBadge } from '../components/TrustBadges';
import BuyerAccountDialog from '../components/BuyerAccountDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAVED_WALLETS_KEY = 'sm_saved_wallets';
const MAX_SAVED = 3;

const loadSavedWallets = () => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_WALLETS_KEY) || '[]');
  } catch { return []; }
};
const persistSavedWallets = (list) => {
  try { localStorage.setItem(SAVED_WALLETS_KEY, JSON.stringify(list)); } catch {}
};

// Receiving wallets (must match backend)
const RECEIVING_WALLETS = {
  base: '0x299290421e696a76ef1efe77adee23c7629613d7',
  ethereum: '0x4A82F8bcDEaBcF56C3bE1f22e5b1B333aFae243f',
};

const CHAIN_IDS = { base: base.id, ethereum: mainnet.id };

const AgentCommerceContent = () => {
  const { address, isConnected, chain: activeChain, connector: activeConnector } = useAccount();
  const { connect, connectors } = useConnect();
  const { reconnect } = useReconnect();
  const { openConnectModal } = useConnectModal();
  const { switchChainAsync } = useSwitchChain();
  const { disconnect } = useDisconnect();
  const [savedWallets, setSavedWallets] = useState(() => loadSavedWallets());
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [rfqCart, setRfqCart] = useState([]); // CALL items
  const [ethPrice, setEthPrice] = useState(null);
  const [usdcContracts, setUsdcContracts] = useState({});
  const [page, setPage] = useState(1);
  const [jumpInput, setJumpInput] = useState('');
  const PAGE_SIZE = 12;

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('details'); // details | review | paying | success
  const [chain, setChain] = useState('base');
  const [token, setToken] = useState('USDC');
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [shippingAddr, setShippingAddr] = useState({
    full_name: '', company: '', street: '', city: '', state: '', zip_code: '', country: 'USA'
  });
  const [shippingCost, setShippingCost] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);

  // RFQ-only dialog (CALL items)
  const [showRfqModal, setShowRfqModal] = useState(false);
  const [rfqContact, setRfqContact] = useState({
    email: '', phone: '', full_name: '', company: '',
    street: '', city: '', state: '', zip: '', country: 'USA',
  });
  const [submittingRfq, setSubmittingRfq] = useState(false);

  // wagmi hooks
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();
  const [txHash, setTxHash] = useState(null);
  const { isLoading: txConfirming, isSuccess: txConfirmed } = useWaitForTransactionReceipt({ hash: txHash });

  useEffect(() => {
    if (isConnected) {
      loadInventory();
      fetchEthPrice();
      axios.get(`${API_URL}/api/agent/orders/usdc-contracts`)
        .then(r => setUsdcContracts(r.data.usdc_contracts || {}))
        .catch(() => {});
    }
  }, [isConnected]);

  // Save wallet to localStorage after successful connect
  useEffect(() => {
    if (!isConnected || !address || !activeConnector) return;
    setSavedWallets(prev => {
      const filtered = prev.filter(w => w.address.toLowerCase() !== address.toLowerCase());
      const next = [{
        address,
        connector_id: activeConnector.id,
        connector_name: activeConnector.name,
        chain_id: activeChain?.id || null,
        chain_name: activeChain?.name || '',
        last_used_at: new Date().toISOString(),
      }, ...filtered].slice(0, MAX_SAVED);
      persistSavedWallets(next);
      return next;
    });
  }, [isConnected, address, activeConnector, activeChain]);

  // Critical for iOS Safari: when user returns from the MetaMask/Coinbase wallet app,
  // Safari has paused our WebSocket. Force wagmi to reconnect so the UI reflects the live session.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        try { reconnect(); } catch (e) { /* noop */ }
      }
    };
    const onFocus = () => { try { reconnect(); } catch (e) {} };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, [reconnect]);

  const reconnectSaved = async (saved) => {
    const matchingConnector = connectors.find(c => c.id === saved.connector_id);
    if (!matchingConnector) {
      toast.error(`${saved.connector_name} connector unavailable — open Connect Wallet instead`);
      openConnectModal?.();
      return;
    }
    try {
      await connect({ connector: matchingConnector });
    } catch (e) {
      toast.error(`Failed to reconnect: ${e.shortMessage || e.message}`);
    }
  };

  const removeSavedWallet = (address) => {
    setSavedWallets(prev => {
      const next = prev.filter(w => w.address.toLowerCase() !== address.toLowerCase());
      persistSavedWallets(next);
      return next;
    });
    toast.info('Wallet removed from saved list');
  };

  // Direct-connect helpers — skip the RainbowKit wallet-picker modal entirely
  const findConnector = (keywords) => {
    const kws = (Array.isArray(keywords) ? keywords : [keywords]).map(k => k.toLowerCase());
    return connectors.find(c => {
      const id = (c.id || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      return kws.some(k => id.includes(k) || name.includes(k));
    });
  };

  // Mobile detection (iOS / Android)
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isInjected = typeof window !== 'undefined' && !!window.ethereum;
  // Detect which wallet's in-app browser we're inside (prevents cross-wallet SDK popups)
  const inMetaMaskBrowser = typeof window !== 'undefined' && !!window.ethereum?.isMetaMask && !window.ethereum?.isCoinbaseWallet;
  const inCoinbaseBrowser = typeof window !== 'undefined' && !!window.ethereum?.isCoinbaseWallet;

  // Nuclear reset — clears all wagmi + saved wallets state and reloads. Use when stuck with a dead session.
  const resetWalletState = () => {
    try { disconnect(); } catch (e) {}
    try {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('wagmi') || k.startsWith('wc@') || k.startsWith('@w3m') || k === 'sm_saved_wallets' || k.includes('walletconnect') || k.includes('coinbase')) {
          localStorage.removeItem(k);
        }
      });
      sessionStorage.clear();
    } catch (e) {}
    toast.info('Wallet state cleared — reloading');
    setTimeout(() => window.location.reload(), 500);
  };

  // Deep-link fallbacks — open the dApp inside the wallet's in-app browser where window.ethereum is available.
  // This is the most reliable mobile pattern — no WalletConnect handshake required.
  const deepLinkTo = (wallet) => {
    const origin = window.location.origin + window.location.pathname;
    const urlNoProto = origin.replace(/^https?:\/\//, '');
    if (wallet === 'metamask') {
      window.location.href = `https://metamask.app.link/dapp/${urlNoProto}`;
    } else if (wallet === 'coinbase') {
      window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(origin)}`;
    }
  };

  const connectDirect = async (wallet, keywords, label) => {
    // On mobile Safari/Chrome WITHOUT injected ethereum, deep-link straight into the wallet app's browser
    if (isMobile && !isInjected) {
      deepLinkTo(wallet);
      return;
    }
    // Desktop or in-app browser: use wagmi connector
    const c = findConnector(keywords);
    if (!c) {
      toast.error(`${label} unavailable — opening wallet picker`);
      openConnectModal?.();
      return;
    }
    try {
      await connect({ connector: c });
    } catch (e) {
      toast.error(`${label} connect failed: ${e.shortMessage || e.message || ''}`);
    }
  };

  const fetchEthPrice = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/agent/orders/eth-price`);
      setEthPrice(r.data.price_usd);
    } catch (e) {
      // silent
    }
  };

  const loadInventory = async (search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '500');
      const response = await axios.get(`${API_URL}/api/agent/inventory?${params.toString()}`);
      setInventory(response.data.items || []);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadInventory(searchQuery);

  const addToCart = (item, qty = 1) => {
    const q = Math.max(1, parseInt(qty) || 1);
    if (item.price_type === 'call') {
      const existing = rfqCart.find(c => c.part_number === item.part_number);
      if (existing) {
        setRfqCart(rfqCart.map(c => c.part_number === item.part_number ? { ...c, quantity: c.quantity + q } : c));
        toast.success(`+${q} added to Quote Request`);
        return;
      }
      setRfqCart([...rfqCart, { ...item, quantity: q }]);
      toast.success(`${item.part_number} × ${q} added to Quote Request`);
      return;
    }
    const existing = cart.find(c => c.part_number === item.part_number);
    if (existing) {
      setCart(cart.map(c => c.part_number === item.part_number ? { ...c, quantity: c.quantity + q } : c));
      toast.success(`+${q} added to cart`);
      return;
    }
    setCart([...cart, { ...item, quantity: q }]);
    toast.success(`${item.part_number} × ${q} added to cart`);
  };

  const updateQty = (list, setList) => (partNumber, newQty) => {
    const n = parseInt(newQty);
    if (!isFinite(n) || n < 1) {
      // Qty dropped to 0 (or invalid) → remove item
      setList(list.filter(i => i.part_number !== partNumber));
      return;
    }
    setList(list.map(i => i.part_number === partNumber ? { ...i, quantity: n } : i));
  };
  const updateCartQty = updateQty(cart, setCart);
  const updateRfqQty = updateQty(rfqCart, setRfqCart);

  const removeFromCart = (pn) => setCart(cart.filter(i => i.part_number !== pn));
  const removeFromRfq = (pn) => setRfqCart(rfqCart.filter(i => i.part_number !== pn));

  const cartSubtotal = useMemo(() =>
    cart.reduce((s, i) => s + (i.unit_price_usd * i.quantity), 0), [cart]);
  const cartTotalQty = useMemo(() =>
    cart.reduce((s, i) => s + i.quantity, 0), [cart]);
  const isInternational = (shippingAddr.country || 'USA').toUpperCase() !== 'USA';
  const grandTotal = cartSubtotal + shippingCost;

  // Recalculate shipping when qty/country changes
  useEffect(() => {
    if (!showCheckout || cartTotalQty === 0) return;
    // Free shipping when cart contains ONLY the test SKU (used for real-wallet smoke testing)
    const testOnly = cart.length > 0 && cart.every(i => (i.part_number || '').toLowerCase() === 'test');
    if (testOnly) {
      setShippingCost(0);
      return;
    }
    const qty = cartTotalQty;
    let base = 15;
    if (qty > 500) base = 150;
    else if (qty > 100) base = 75;
    else if (qty > 50) base = 25;
    setShippingCost(isInternational ? base + 20 : base);
  }, [cartTotalQty, shippingAddr.country, showCheckout, cart, isInternational]);

  const ethAmount = useMemo(() => {
    if (!ethPrice || token !== 'ETH') return null;
    return (grandTotal / ethPrice).toFixed(6);
  }, [grandTotal, ethPrice, token]);

  const openCheckout = () => {
    if (cart.length === 0) return toast.error('Cart is empty');
    setCheckoutStep('details');
    setShowCheckout(true);
  };

  const validateCheckoutForm = () => {
    if (!contact.email || !contact.phone) return 'Email and phone required';
    const a = shippingAddr;
    if (!a.full_name || !a.street || !a.city || !a.state || !a.zip_code) return 'Complete shipping address';
    if (token === 'ETH' && !ethPrice) return 'ETH price not available';
    if (token === 'USDC' && !usdcContracts[chain]) return 'USDC not configured on this chain';
    return null;
  };

  const executePayment = async () => {
    const targetChainId = CHAIN_IDS[chain];
    if (activeChain?.id !== targetChainId) {
      try {
        await switchChainAsync({ chainId: targetChainId });
      } catch (e) {
        throw new Error(`Please switch wallet to ${chain === 'base' ? 'Base' : 'Ethereum'} network`);
      }
    }
    const to = RECEIVING_WALLETS[chain];
    if (token === 'ETH') {
      const ethValue = parseEther(ethAmount);
      const hash = await sendTransactionAsync({ to, value: ethValue });
      return hash;
    } else {
      // USDC - ERC20 transfer
      const usdcAddr = usdcContracts[chain];
      const amountUnits = parseUnits(grandTotal.toFixed(2), 6); // USDC has 6 decimals
      const hash = await writeContractAsync({
        address: usdcAddr,
        abi: erc20Abi,
        functionName: 'transfer',
        args: [to, amountUnits],
      });
      return hash;
    }
  };

  const handleSubmitOrder = async () => {
    const err = validateCheckoutForm();
    if (err) return toast.error(err);
    setSubmitting(true);
    setCheckoutStep('paying');
    try {
      toast.info('Please confirm the transaction in your wallet…');
      const hash = await executePayment();
      setTxHash(hash);
      toast.success('Transaction submitted. Recording order…');

      const items = cart.map(i => ({
        part_number: i.part_number,
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price_usd,
        line_total: i.unit_price_usd * i.quantity,
      }));
      const res = await axios.post(`${API_URL}/api/agent/orders/create`, {
        wallet_address: address,
        chain,
        payment_token: token,
        payment_tx_hash: hash,
        eth_price_at_order: token === 'ETH' ? ethPrice : null,
        items,
        subtotal: cartSubtotal,
        shipping_method: isInternational ? 'ground_international' : 'ground_usa',
        shipping_cost: shippingCost,
        total_usd: grandTotal,
        contact,
        shipping_address: shippingAddr,
      });
      setOrderResult(res.data);
      setCheckoutStep('success');
      setCart([]);

      // If pending_verification, poll /verify every 6s up to 20 tries
      if (res.data.status === 'pending_verification') {
        pollVerification(res.data.order_id);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === 'object' ? `${d.message} (${d.reason})` : d) || e?.shortMessage || e?.message || 'Payment failed';
      toast.error(msg);
      setCheckoutStep('details');
    } finally {
      setSubmitting(false);
    }
  };

  const pollVerification = async (orderId) => {
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 6000));
      try {
        const r = await axios.post(`${API_URL}/api/agent/orders/${orderId}/verify`);
        if (r.data.status === 'paid') {
          toast.success('Payment confirmed on-chain ✓');
          setOrderResult(prev => ({ ...prev, status: 'paid', verification: r.data.verification }));
          return;
        }
        if (r.data.status === 'pending' && r.data.verification?.status === 'failed') {
          toast.error(`Verification failed: ${r.data.verification.detail}`);
          return;
        }
      } catch (e) { /* keep polling */ }
    }
  };

  const openRfqModal = () => {
    if (rfqCart.length === 0) return toast.error('Nothing in quote list');
    setShowRfqModal(true);
  };

  const handleSubmitRfq = async () => {
    if (!rfqContact.email) return toast.error('Email required');
    if (!rfqContact.full_name.trim()) return toast.error('Full name required');
    if (!rfqContact.street.trim() || !rfqContact.city.trim() || !rfqContact.zip.trim()) {
      return toast.error('Shipping address (street, city, ZIP) required so we can quote freight');
    }
    setSubmittingRfq(true);
    try {
      const items = rfqCart.map(i => ({
        part_number: i.part_number,
        description: i.description,
        quantity: i.quantity,
        condition: i.condition,
        taa: i.taa_compliant ? 'Yes' : 'No',
        quantity_available: i.quantity_available,
      }));
      const res = await axios.post(`${API_URL}/api/agent/orders/submit-rfq`, {
        wallet_address: address,
        chain,
        contact: { email: rfqContact.email, phone: rfqContact.phone },
        shipping_address: {
          full_name: rfqContact.full_name,
          company: rfqContact.company,
          street: rfqContact.street,
          city: rfqContact.city,
          state: rfqContact.state,
          zip: rfqContact.zip,
          zip_code: rfqContact.zip,
          country: rfqContact.country,
        },
        items,
      });
      toast.success(`Quote request submitted! RFQ #${res.data.rfq_id.slice(0, 8)}`);
      setRfqCart([]);
      setShowRfqModal(false);
      setRfqContact({
        email: '', phone: '', full_name: '', company: '',
        street: '', city: '', state: '', zip: '', country: 'USA',
      });
    } catch (e) {
      toast.error('Failed to submit RFQ');
    } finally {
      setSubmittingRfq(false);
    }
  };

  const chainsList = [
    { id: 'base', name: 'Base', color: '#0052FF', icon: '🔵' },
    { id: 'ethereum', name: 'Ethereum', color: '#627EEA', icon: '⟠' },
    { id: 'stx', name: 'Stacks (BTC)', color: '#5546FF', icon: '₿', note: 'Coming Soon' },
    { id: 'solana', name: 'Solana', color: '#9945FF', icon: '◎', note: 'Coming Soon' }
  ];

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Robot size={40} className="text-cyan-400" weight="fill" />
            <h1 className="text-3xl md:text-4xl font-['JetBrains_Mono'] font-bold">AI Agent Commerce</h1>
          </div>
          <p className="text-[#94A3B8] text-base md:text-lg max-w-2xl mx-auto">
            Connect your Web3 wallet for instant on-chain checkout. CALL items are auto-routed to a human sales rep.
          </p>
          {ethPrice && (
            <p className="text-xs text-[#10B981] mt-2 font-mono" data-testid="eth-price-display">
              ETH ≈ ${ethPrice.toFixed(2)} USD
            </p>
          )}
        </div>

        {/* Supplier reputation + escrow trust banner */}
        <div className="mb-6">
          <SupplierTrustBadge />
        </div>

        {/* Wallet Connection */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mb-8">
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-xl font-['JetBrains_Mono'] font-bold flex items-center gap-2">
              <Shield size={24} className="text-[#10B981]" /> Connect Your Wallet
            </h2>

            {!isConnected && (
              <div className="w-full flex flex-col gap-2">
                {inMetaMaskBrowser && (
                  <div className="text-xs text-[#10B981] text-center bg-[#10B981]/10 border border-[#10B981]/30 rounded p-2">
                    🦊 You're inside MetaMask's browser — tap Connect MetaMask
                  </div>
                )}
                {inCoinbaseBrowser && (
                  <div className="text-xs text-[#0052FF] text-center bg-[#0052FF]/10 border border-[#0052FF]/30 rounded p-2">
                    🔵 You're inside Coinbase Wallet's browser — tap Connect Coinbase
                  </div>
                )}
                <Button
                  onClick={() => connectDirect('coinbase', ['coinbase'], 'Coinbase Wallet')}
                  disabled={inMetaMaskBrowser}
                  className={`w-full font-['JetBrains_Mono'] h-12 text-base ${inMetaMaskBrowser ? 'bg-[#334155] text-[#64748B] cursor-not-allowed' : 'bg-[#0052FF] hover:bg-[#0041CC] text-white'}`}
                  data-testid="connect-coinbase-direct"
                  title={inMetaMaskBrowser ? 'Close MetaMask browser and open in Safari to use Coinbase' : ''}
                >
                  <span className="mr-2 text-xl">🔵</span>
                  {inMetaMaskBrowser ? 'Coinbase — exit MetaMask browser first' : 'Connect Coinbase / Base Wallet'}
                </Button>
                <Button
                  onClick={() => connectDirect('metamask', ['metamask', 'io.metamask'], 'MetaMask')}
                  disabled={inCoinbaseBrowser}
                  className={`w-full font-['JetBrains_Mono'] h-12 text-base ${inCoinbaseBrowser ? 'bg-[#334155] text-[#64748B] cursor-not-allowed' : 'bg-[#F6851B] hover:bg-[#E2761B] text-white'}`}
                  data-testid="connect-metamask-direct"
                  title={inCoinbaseBrowser ? 'Close Coinbase browser and open in Safari to use MetaMask' : ''}
                >
                  <span className="mr-2 text-xl">🦊</span>
                  {inCoinbaseBrowser ? 'MetaMask — exit Coinbase browser first' : 'Connect MetaMask'}
                </Button>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => openConnectModal?.()}
                    className="text-xs text-[#94A3B8] hover:text-white underline"
                    data-testid="more-wallets-link"
                  >
                    More wallets…
                  </button>
                  <button
                    type="button"
                    onClick={resetWalletState}
                    className="text-xs text-[#F59E0B] hover:text-[#EF4444] underline"
                    data-testid="reset-wallet-btn"
                    title="Clears all wallet session data and reloads"
                  >
                    Stuck? Reset wallet state
                  </button>
                </div>
              </div>
            )}

            {isConnected && (
              <div className="w-full flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 bg-[#10B981]/10 border border-[#10B981] rounded px-3 py-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></div>
                  <p className="text-[#10B981] text-sm font-['JetBrains_Mono']">Connected on {activeChain?.name || 'Unknown'}</p>
                </div>
                <p className="text-[#94A3B8] text-xs font-mono">{address?.slice(0, 8)}...{address?.slice(-6)}</p>
                <WalletReputationBadge address={address} />
                <div className="flex gap-2 mt-1">
                  <Button
                    onClick={() => setAccountOpen(true)}
                    variant="outline"
                    size="sm"
                    className="border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
                    data-testid="account-modal-btn"
                  >
                    Account
                  </Button>
                  <Button
                    onClick={() => { disconnect(); toast.info('Wallet disconnected'); }}
                    variant="outline"
                    size="sm"
                    className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                    data-testid="disconnect-wallet-btn"
                  >
                    Disconnect
                  </Button>
                  <Button
                    onClick={resetWalletState}
                    variant="outline"
                    size="sm"
                    className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                    data-testid="reset-wallet-connected-btn"
                    title="Fully clears all wallet cache and reloads"
                  >
                    Reset
                  </Button>
                </div>
              </div>
            )}
            {!isConnected && savedWallets.length > 0 && (
              <div className="w-full mt-2 mb-4" data-testid="saved-wallets-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} className="text-[#10B981]" />
                  <p className="text-[#10B981] text-sm font-['JetBrains_Mono']">Welcome back — pick up where you left off</p>
                </div>
                <div className="space-y-2">
                  {savedWallets.map(w => (
                    <div key={w.address} className="flex items-center gap-2 bg-[#1E293B] rounded-lg p-3" data-testid={`saved-wallet-${w.address}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-['JetBrains_Mono']">{w.connector_name}</p>
                        <p className="text-xs text-cyan-400 font-mono truncate">{w.address.slice(0, 10)}…{w.address.slice(-6)}</p>
                        <p className="text-xs text-[#94A3B8]">Last used {new Date(w.last_used_at).toLocaleDateString()} · {w.chain_name || 'Unknown'}</p>
                      </div>
                      <Button
                        onClick={() => reconnectSaved(w)}
                        size="sm"
                        className="bg-[#10B981] hover:bg-[#059669] text-xs"
                        data-testid={`reconnect-${w.address}`}
                      >
                        Reconnect
                      </Button>
                      <Button
                        onClick={() => removeSavedWallet(w.address)}
                        variant="ghost"
                        size="sm"
                        className="text-[#EF4444] p-1 h-auto"
                        data-testid={`remove-saved-${w.address}`}
                        title="Remove from saved"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] text-center mt-2">or use a different wallet below</p>
              </div>
            )}

            {!isConnected && (
              <div className="w-full mt-4">
                <p className="text-[#94A3B8] text-sm text-center mb-3">Supported Networks:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {chainsList.map(c => {
                    const isActive = !c.note;
                    // Base card → direct Coinbase Wallet; Ethereum card → direct MetaMask
                    const directWallet = c.id === 'base' ? 'coinbase' : c.id === 'ethereum' ? 'metamask' : null;
                    const directKeywords = c.id === 'base' ? ['coinbase'] : c.id === 'ethereum' ? ['metamask', 'io.metamask'] : null;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          if (!isActive) {
                            toast.info(`${c.name} support coming soon`);
                            return;
                          }
                          if (directWallet) {
                            connectDirect(directWallet, directKeywords, c.name);
                          } else {
                            openConnectModal?.();
                          }
                        }}
                        className={`bg-[#1E293B] rounded-lg p-3 text-center transition ${isActive ? 'hover:bg-[#334155] hover:border-[#0047AB] border border-transparent cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                        data-testid={`network-card-${c.id}`}
                      >
                        <span className="text-2xl">{c.icon}</span>
                        <p className="text-white text-sm mt-1">{c.name}</p>
                        {c.note && <p className="text-[#F59E0B] text-xs">{c.note}</p>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {isConnected && (
          <>
            {/* Configurator */}
            <AgentConfigurator
              onApply={({ display, api }) => {
                setSearchQuery(display);
                loadInventory(api);
              }}
            />

            {/* Search */}
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search inventory (e.g., DDR5 128GB)"
                  className="bg-transparent border-[#334155] flex-1"
                  data-testid="agent-search-input"
                />
                <Button onClick={handleSearch} className="bg-[#0047AB]" data-testid="agent-search-btn">Search</Button>
              </div>
            </div>

            {/* Paid Cart */}
            {cart.length > 0 && (
              <div className="bg-[#10B981]/10 border border-[#10B981] rounded-lg p-4 mb-4" data-testid="paid-cart">
                <h3 className="font-['JetBrains_Mono'] font-bold text-[#10B981] mb-3 flex items-center gap-2">
                  <ShoppingCart size={18} /> Cart — Instant Pay ({cart.length})
                </h3>
                <div className="space-y-2">
                  {cart.map(item => (
                    <CartRow key={item.part_number} item={item}
                      onQty={(q) => updateCartQty(item.part_number, q)}
                      onRemove={() => removeFromCart(item.part_number)}
                      showPrice />
                  ))}
                </div>
                <div className="flex flex-wrap justify-between items-center mt-3 pt-3 border-t border-[#10B981]/30 gap-2">
                  <div>
                    <p className="text-white font-bold text-lg">Subtotal: ${cartSubtotal.toFixed(2)}</p>
                    <p className="text-xs text-[#94A3B8]">
                      {cartTotalQty} units · {cart.length > 0 && cart.every(i => (i.part_number || '').toLowerCase() === 'test')
                        ? 'free shipping (test SKU)'
                        : 'shipping calculated at checkout'}
                    </p>
                  </div>
                  <Button onClick={openCheckout} className="bg-[#10B981] hover:bg-[#059669]" data-testid="checkout-btn">
                    <Coins size={18} className="mr-2" /> Checkout
                  </Button>
                </div>
              </div>
            )}

            {/* RFQ Cart */}
            {rfqCart.length > 0 && (
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B] rounded-lg p-4 mb-6" data-testid="rfq-cart">
                <h3 className="font-['JetBrains_Mono'] font-bold text-[#F59E0B] mb-3 flex items-center gap-2">
                  <Package size={18} /> Quote Request — Human Handled ({rfqCart.length})
                </h3>
                <div className="space-y-2">
                  {rfqCart.map(item => (
                    <CartRow key={item.part_number} item={item}
                      onQty={(q) => updateRfqQty(item.part_number, q)}
                      onRemove={() => removeFromRfq(item.part_number)} />
                  ))}
                </div>
                <div className="flex justify-end mt-3 pt-3 border-t border-[#F59E0B]/30">
                  <Button onClick={openRfqModal} className="bg-[#F59E0B] hover:bg-[#D97706] text-black" data-testid="rfq-submit-btn">
                    Request Quote
                  </Button>
                </div>
              </div>
            )}

            {/* Inventory Grid */}
            {(() => {
              const totalPages = Math.max(1, Math.ceil(inventory.length / PAGE_SIZE));
              const safePage = Math.min(page, totalPages);
              const startIdx = (safePage - 1) * PAGE_SIZE;
              const pagedItems = inventory.slice(startIdx, startIdx + PAGE_SIZE);
              return (
                <>
                  <div className="flex items-center justify-between mb-3 text-sm text-[#94A3B8] font-['JetBrains_Mono']">
                    <span data-testid="inventory-total">
                      {inventory.length} {inventory.length === 1 ? 'item' : 'items'}
                    </span>
                    {inventory.length > 0 && (
                      <span data-testid="page-indicator">
                        Page {safePage} of {totalPages}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agent-inventory-grid">
                    {loading ? (
                      <p className="col-span-full text-center text-[#94A3B8] py-8">Loading inventory...</p>
                    ) : pagedItems.length === 0 ? (
                      <div className="col-span-full text-center py-8">
                        <Package size={48} className="mx-auto text-[#94A3B8] mb-3" />
                        <p className="text-[#94A3B8]">No inventory found.</p>
                      </div>
                    ) : (
                      pagedItems.map((item, idx) => (
                        <InventoryCard
                          key={`${safePage}-${item.part_number}-${idx}`}
                          item={item}
                          onAdd={(qty) => addToCart(item, qty)}
                        />
                      ))
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {inventory.length > PAGE_SIZE && (
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm" data-testid="pagination-controls">
                      <Button
                        onClick={() => { setPage(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={safePage === 1}
                        variant="outline"
                        size="sm"
                        className="border-[#334155] font-['JetBrains_Mono']"
                        data-testid="page-first"
                      >
                        First
                      </Button>
                      <Button
                        onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={safePage === 1}
                        variant="outline"
                        size="sm"
                        className="border-[#334155] font-['JetBrains_Mono']"
                        data-testid="page-prev"
                      >
                        <CaretLeft size={14} />
                      </Button>
                      <span className="px-3 text-[#94A3B8] font-['JetBrains_Mono']">
                        {safePage} / {totalPages}
                      </span>
                      <Button
                        onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={safePage === totalPages}
                        variant="outline"
                        size="sm"
                        className="border-[#334155] font-['JetBrains_Mono']"
                        data-testid="page-next"
                      >
                        <CaretRight size={14} />
                      </Button>
                      <Button
                        onClick={() => { setPage(totalPages); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={safePage === totalPages}
                        variant="outline"
                        size="sm"
                        className="border-[#334155] font-['JetBrains_Mono']"
                        data-testid="page-last"
                      >
                        Last
                      </Button>
                      <div className="flex items-center gap-1 ml-3">
                        <span className="text-xs text-[#94A3B8]">Jump to:</span>
                        <Input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={jumpInput}
                          onChange={(e) => setJumpInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const n = parseInt(jumpInput);
                              if (n >= 1 && n <= totalPages) {
                                setPage(n);
                                setJumpInput('');
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            }
                          }}
                          placeholder={`1-${totalPages}`}
                          className="w-20 h-8 bg-transparent border-[#334155] text-center text-sm"
                          data-testid="page-jump-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* API Documentation */}
        <div className="mt-12 bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="agent-api-docs">
          <h2 className="text-xl font-['JetBrains_Mono'] font-bold mb-4 flex items-center gap-2">
            <Lightning size={24} className="text-[#F59E0B]" /> For AI Agents & Developers
          </h2>

          {/* Discoverability badges */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="agent-badges">
            <a href="/.well-known/agent-manifest.json" target="_blank" rel="noreferrer"
               className="px-3 py-1 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-xs font-['JetBrains_Mono'] hover:bg-[#10B981]/20 transition"
               data-testid="badge-agent-manifest">/.well-known/agent-manifest.json</a>
            <a href="/llms.txt" target="_blank" rel="noreferrer"
               className="px-3 py-1 rounded-full border border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF] text-xs font-['JetBrains_Mono'] hover:bg-[#007AFF]/20 transition"
               data-testid="badge-llms-txt">/llms.txt</a>
            <a href="/.well-known/ai-plugin.json" target="_blank" rel="noreferrer"
               className="px-3 py-1 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7] text-xs font-['JetBrains_Mono'] hover:bg-[#A855F7]/20 transition"
               data-testid="badge-ai-plugin">ai-plugin.json</a>
            <a href="/api/agent/mcp/context" target="_blank" rel="noreferrer"
               className="px-3 py-1 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-['JetBrains_Mono'] hover:bg-cyan-400/20 transition"
               data-testid="badge-mcp">MCP v1.0</a>
            <a href="/api/agent/x402/" target="_blank" rel="noreferrer"
               className="px-3 py-1 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-['JetBrains_Mono'] hover:bg-[#F59E0B]/20 transition"
               data-testid="badge-x402">x402 v1 · HTTP 402</a>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm mb-2">Discovery &amp; Manifest</h3>
              <div className="bg-[#05050A] rounded p-3 font-mono text-xs space-y-1">
                <p><span className="text-[#10B981]">GET</span> /api/agent/manifest</p>
                <p><span className="text-[#10B981]">GET</span> /api/agent/inventory</p>
                <p><span className="text-[#10B981]">GET</span> /api/agent/pricing/{'{part}'}</p>
                <p><span className="text-[#10B981]">GET</span> /api/agent/orders/eth-price</p>
                <p><span className="text-[#F59E0B]">POST</span> /api/agent/orders/create</p>
                <p><span className="text-[#F59E0B]">POST</span> /api/agent/orders/submit-rfq</p>
              </div>
            </div>
            <div>
              <h3 className="font-['JetBrains_Mono'] text-[#F59E0B] text-sm mb-2">x402 Machine Payments</h3>
              <div className="bg-[#05050A] rounded p-3 font-mono text-xs space-y-1">
                <p><span className="text-[#10B981]">GET</span> /api/agent/x402/ <span className="text-[#64748B]"># discovery</span></p>
                <p><span className="text-[#10B981]">GET</span> /api/agent/x402/feed <span className="text-[#64748B]"># 0.10 USDC</span></p>
                <p><span className="text-[#F59E0B]">POST</span> /api/agent/x402/purchase/{'{part}'}</p>
                <p className="text-[#64748B] mt-2"># Unpaid → 402 + X-PAYMENT-REQUIRED</p>
                <p className="text-[#64748B]"># Retry with X-PAYMENT (USDC on Base)</p>
                <p className="text-[#10B981] mt-1">Scheme: exact-txhash</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-4">
            This site is indexed for autonomous agents. Point any MCP-compatible LLM (Claude, GPT-5, Gemini 3) at
            <code className="text-cyan-300 mx-1">/api/agent/mcp/context</code>and it can transact here without a human.
          </p>
        </div>
      </div>

      <Footer />

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={(o) => !submitting && setShowCheckout(o)}>
        <DialogContent className="max-w-2xl bg-[#11141D] border-[#1E293B] text-white max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#10B981]">
              {checkoutStep === 'success' ? 'Order Confirmed' : 'Web3 Checkout'}
            </DialogTitle>
          </DialogHeader>

          {checkoutStep === 'success' && orderResult ? (
            <div className="space-y-3 py-4">
              {orderResult.status === 'paid' ? (
                <p className="text-[#10B981] text-lg">✓ Payment verified on-chain</p>
              ) : (
                <p className="text-[#F59E0B] text-lg">⏳ Verifying payment on-chain…</p>
              )}
              <p className="font-mono text-sm">Order ID: <span className="text-cyan-400">{orderResult.order_id}</span></p>
              <p className="text-xs text-[#94A3B8] break-all">Tx: {orderResult.payment_tx_hash}</p>
              {orderResult.status === 'pending_verification' && (
                <p className="text-xs text-[#94A3B8]">Your tx has been submitted. We're polling the chain every 6s — please keep this window open. Once confirmed, status flips to paid automatically.</p>
              )}
              {orderResult.verification?.observed?.block_number && (
                <p className="text-xs text-[#10B981]">Confirmed in block #{orderResult.verification.observed.block_number}</p>
              )}
              <p className="text-sm">Order status: wallet API <code className="text-cyan-400 text-xs">/api/agent/orders/wallet/{address}</code></p>
              <a
                href={`/order/${orderResult.order_id}`}
                target="_blank"
                rel="noreferrer"
                className="block text-center w-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-['JetBrains_Mono'] text-sm rounded px-4 py-2"
                data-testid="open-buyer-order-link"
              >
                Bookmark / open your order page →
              </a>
              <Button onClick={() => setShowCheckout(false)} className="bg-[#0047AB] w-full" data-testid="close-success">Done</Button>
            </div>
          ) : (
            <>
              {/* Contact */}
              <div className="space-y-3">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm">Contact</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#94A3B8]">Email *</Label>
                    <Input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      className="bg-transparent border-[#334155] text-white" data-testid="checkout-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94A3B8]">Phone *</Label>
                    <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                      className="bg-transparent border-[#334155] text-white" data-testid="checkout-phone" />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="space-y-3">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm">Shipping Address</h4>
                <Input placeholder="Full name *" value={shippingAddr.full_name}
                  onChange={(e) => setShippingAddr({ ...shippingAddr, full_name: e.target.value })}
                  className="bg-transparent border-[#334155] text-white" data-testid="ship-fullname" />
                <Input placeholder="Company (optional)" value={shippingAddr.company}
                  onChange={(e) => setShippingAddr({ ...shippingAddr, company: e.target.value })}
                  className="bg-transparent border-[#334155] text-white" />
                <Input placeholder="Street address *" value={shippingAddr.street}
                  onChange={(e) => setShippingAddr({ ...shippingAddr, street: e.target.value })}
                  className="bg-transparent border-[#334155] text-white" data-testid="ship-street" />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="City *" value={shippingAddr.city}
                    onChange={(e) => setShippingAddr({ ...shippingAddr, city: e.target.value })}
                    className="bg-transparent border-[#334155] text-white" data-testid="ship-city" />
                  <Input placeholder="State *" value={shippingAddr.state}
                    onChange={(e) => setShippingAddr({ ...shippingAddr, state: e.target.value })}
                    className="bg-transparent border-[#334155] text-white" data-testid="ship-state" />
                  <Input placeholder="ZIP *" value={shippingAddr.zip_code}
                    onChange={(e) => setShippingAddr({ ...shippingAddr, zip_code: e.target.value })}
                    className="bg-transparent border-[#334155] text-white" data-testid="ship-zip" />
                </div>
                <Input placeholder="Country" value={shippingAddr.country}
                  onChange={(e) => setShippingAddr({ ...shippingAddr, country: e.target.value })}
                  className="bg-transparent border-[#334155] text-white" data-testid="ship-country" />
              </div>

              {/* Payment method */}
              <div className="space-y-3">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm">Payment</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#94A3B8]">Chain</Label>
                    <Select value={chain} onValueChange={setChain}>
                      <SelectTrigger className="bg-transparent border-[#334155] text-white" data-testid="chain-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#11141D] text-white border-[#334155]">
                        <SelectItem value="base">Base</SelectItem>
                        <SelectItem value="ethereum">Ethereum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#94A3B8]">Token</Label>
                    <Select value={token} onValueChange={setToken}>
                      <SelectTrigger className="bg-transparent border-[#334155] text-white" data-testid="token-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#11141D] text-white border-[#334155]">
                        <SelectItem value="ETH">ETH (native)</SelectItem>
                        {chain === 'base' && <SelectItem value="USDC">USDC (Base)</SelectItem>}
                        {chain === 'ethereum' && <SelectItem value="USDC">USDC (Ethereum)</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-xs text-[#94A3B8] font-mono break-all">
                  → Pays to {RECEIVING_WALLETS[chain]}
                </div>
                {address && address.toLowerCase() === RECEIVING_WALLETS[chain]?.toLowerCase() && (
                  <div className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded p-2" data-testid="self-pay-warning">
                    ⚠ You're paying FROM and TO the same wallet — USDC/ETH will return to your balance but you'll still lose gas fees. This is normal if you're the merchant testing.
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="bg-[#05050A] rounded p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal ({cartTotalQty} pcs)</span><span>${cartSubtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Shipping ({isInternational ? 'Intl' : 'USA'} Ground)</span><span>${shippingCost.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-[#10B981] pt-2 border-t border-[#1E293B]">
                  <span>Total</span>
                  <span data-testid="grand-total">
                    ${grandTotal.toFixed(2)}
                    {token === 'ETH' && ethAmount && <span className="text-xs text-[#94A3B8] ml-2">≈ {ethAmount} ETH</span>}
                  </span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCheckout(false)} disabled={submitting} className="border-[#334155]">Cancel</Button>
                <Button onClick={handleSubmitOrder} disabled={submitting || checkoutStep === 'paying'}
                  className="bg-[#10B981] hover:bg-[#059669]" data-testid="submit-order-btn">
                  {checkoutStep === 'paying' ? 'Processing…' : `Pay ${token === 'ETH' && ethAmount ? `${ethAmount} ETH` : `${grandTotal.toFixed(2)} USDC`}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* RFQ dialog for CALL items */}
      <Dialog open={showRfqModal} onOpenChange={(o) => !submittingRfq && setShowRfqModal(o)}>
        <DialogContent className="max-w-md bg-[#11141D] border-[#1E293B] text-white max-h-[90vh] overflow-y-auto" data-testid="rfq-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#F59E0B]">Request Quote (Human Handled)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#94A3B8]">These items require a custom quote. A sales rep will contact you with pricing and freight.</p>
          <div className="space-y-3">
            <p className="text-xs text-[#F59E0B] font-['JetBrains_Mono'] uppercase tracking-wider">Contact</p>
            <Input placeholder="Full name *" value={rfqContact.full_name}
              onChange={(e) => setRfqContact({ ...rfqContact, full_name: e.target.value })}
              className="bg-transparent border-[#334155] text-white" data-testid="rfq-name" />
            <Input placeholder="Company" value={rfqContact.company}
              onChange={(e) => setRfqContact({ ...rfqContact, company: e.target.value })}
              className="bg-transparent border-[#334155] text-white" data-testid="rfq-company" />
            <Input placeholder="Email *" value={rfqContact.email}
              onChange={(e) => setRfqContact({ ...rfqContact, email: e.target.value })}
              className="bg-transparent border-[#334155] text-white" data-testid="rfq-email" />
            <Input placeholder="Phone" value={rfqContact.phone}
              onChange={(e) => setRfqContact({ ...rfqContact, phone: e.target.value })}
              className="bg-transparent border-[#334155] text-white" data-testid="rfq-phone" />

            <p className="text-xs text-[#F59E0B] font-['JetBrains_Mono'] uppercase tracking-wider pt-2">Shipping Address</p>
            <Input placeholder="Street address *" value={rfqContact.street}
              onChange={(e) => setRfqContact({ ...rfqContact, street: e.target.value })}
              className="bg-transparent border-[#334155] text-white" data-testid="rfq-street" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="City *" value={rfqContact.city}
                onChange={(e) => setRfqContact({ ...rfqContact, city: e.target.value })}
                className="bg-transparent border-[#334155] text-white" data-testid="rfq-city" />
              <Input placeholder="State / Region" value={rfqContact.state}
                onChange={(e) => setRfqContact({ ...rfqContact, state: e.target.value })}
                className="bg-transparent border-[#334155] text-white" data-testid="rfq-state" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="ZIP / Postal *" value={rfqContact.zip}
                onChange={(e) => setRfqContact({ ...rfqContact, zip: e.target.value })}
                className="bg-transparent border-[#334155] text-white" data-testid="rfq-zip" />
              <Input placeholder="Country" value={rfqContact.country}
                onChange={(e) => setRfqContact({ ...rfqContact, country: e.target.value })}
                className="bg-transparent border-[#334155] text-white" data-testid="rfq-country" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRfqModal(false)} disabled={submittingRfq} className="border-[#334155]">Cancel</Button>
            <Button onClick={handleSubmitRfq} disabled={submittingRfq} className="bg-[#F59E0B] hover:bg-[#D97706] text-black" data-testid="rfq-submit-confirm">
              {submittingRfq ? 'Submitting…' : 'Submit RFQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buyer Account Dialog */}
      <BuyerAccountDialog
        open={accountOpen}
        onOpenChange={setAccountOpen}
        address={address}
        onDispute={(orderId) => {
          setAccountOpen(false);
          window.open(`/order/${orderId}`, '_blank');
        }}
      />
    </div>
  );
};

// Cart row sub-component
const CartRow = ({ item, onQty, onRemove, showPrice }) => {
  const avail = parseInt(item.quantity_available) || 0;
  return (
    <div className="flex flex-wrap items-center gap-2 bg-[#05050A]/50 p-2 rounded text-sm">
      <div className="flex-1 min-w-[140px]">
        <p className="font-mono text-cyan-400 truncate">{item.part_number}</p>
        {showPrice && <p className="text-xs text-[#94A3B8]">${item.unit_price_usd}/ea · avail {item.quantity_available}</p>}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => item.quantity <= 1 ? onRemove() : onQty(item.quantity - 1)}
          className="h-9 w-9 p-0 text-[#94A3B8] hover:text-white"
          data-testid={`qty-minus-${item.part_number}`}
          title={item.quantity <= 1 ? 'Remove from cart' : 'Decrease quantity'}
        >
          <Minus size={16} />
        </Button>
        <Input type="number" min="0" value={item.quantity}
          onChange={(e) => onQty(e.target.value)}
          className="w-20 bg-transparent border-[#334155] text-center h-9 text-sm" data-testid={`qty-input-${item.part_number}`} />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onQty(item.quantity + 1)}
          className="h-9 w-9 p-0 text-[#94A3B8] hover:text-white"
          data-testid={`qty-plus-${item.part_number}`}
        >
          <Plus size={16} />
        </Button>
        {avail > 1 && item.quantity < avail && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onQty(avail)}
            className="h-9 px-2 text-xs border-[#334155] text-[#10B981] font-['JetBrains_Mono']"
            data-testid={`qty-max-${item.part_number}`}
            title={`Set to max available (${avail})`}
          >
            MAX
          </Button>
        )}
      </div>
      {showPrice && (
        <p className="text-[#10B981] font-bold w-24 text-right">
          ${(item.unit_price_usd * item.quantity).toFixed(2)}
        </p>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={onRemove}
        className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 h-9 px-3"
        data-testid={`remove-${item.part_number}`}
        title="Remove from cart"
      >
        <Trash size={16} />
      </Button>
    </div>
  );
};

const MEMORY_SIZES = ['4GB', '8GB', '16GB', '32GB', '64GB', '128GB', '256GB', '512GB'];
const MEMORY_TYPES = ['DDR4', 'DDR5'];
const DDR4_SPEEDS = ['2666', '2933', '3200'];
const DDR5_SPEEDS = ['4800', '5600', '6400', '7200', '8800'];
const FORM_FACTORS = ['RDIMM', 'LRDIMM', '3DS LRDIMM', 'UDIMM', 'SODIMM'];

const AgentConfigurator = ({ onApply }) => {
  const [size, setSize] = useState('');
  const [type, setType] = useState('');
  const [speed, setSpeed] = useState('');
  const [form, setForm] = useState('');
  const speeds = type === 'DDR4' ? DDR4_SPEEDS : type === 'DDR5' ? DDR5_SPEEDS : [...DDR4_SPEEDS, ...DDR5_SPEEDS];

  // Build the display query (shown in search box) + api query (sent to backend).
  // Speed is hyphen-prefixed for the API to match "-8800" in descriptions
  // like "DDR5-8800", avoiding false matches inside random part numbers.
  const buildQueries = React.useCallback((_size, _type, _speed, _form) => {
    const displayParts = [_size, _type, _speed, _form].filter(Boolean);
    const apiParts     = [_size, _type, _speed ? `-${_speed}` : '', _form].filter(Boolean);
    return { display: displayParts.join(' '), api: apiParts.join(' ') };
  }, []);

  const applyWith = (nextSize, nextType, nextSpeed, nextForm) => {
    setSize(nextSize); setType(nextType); setSpeed(nextSpeed); setForm(nextForm);
    const { display, api } = buildQueries(nextSize, nextType, nextSpeed, nextForm);
    onApply({ display, api });
  };

  const apply = () => {
    const { display, api } = buildQueries(size, type, speed, form);
    onApply({ display, api });
  };

  const clear = () => {
    setSize(''); setType(''); setSpeed(''); setForm('');
    onApply({ display: '', api: '' });
  };

  return (
    <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 mb-4" data-testid="agent-configurator">
      <div className="flex items-center gap-2 mb-3">
        <Cube size={18} className="text-[#007AFF]" />
        <p className="font-['JetBrains_Mono'] text-sm text-white">Memory Configurator</p>
        <p className="text-xs text-[#94A3B8] ml-auto">Build a query from specs</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Select value={size} onValueChange={(v) => applyWith(v, type, speed, form)}>
          <SelectTrigger className="bg-transparent border-[#334155] text-white text-sm" data-testid="cfg-size">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent className="bg-[#11141D] text-white border-[#334155]">
            {MEMORY_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={(v) => applyWith(size, v, '', form)}>
          <SelectTrigger className="bg-transparent border-[#334155] text-white text-sm" data-testid="cfg-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#11141D] text-white border-[#334155]">
            {MEMORY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={speed} onValueChange={(v) => applyWith(size, type, v, form)}>
          <SelectTrigger className="bg-transparent border-[#334155] text-white text-sm" data-testid="cfg-speed">
            <SelectValue placeholder="Speed" />
          </SelectTrigger>
          <SelectContent className="bg-[#11141D] text-white border-[#334155]">
            {speeds.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={form} onValueChange={(v) => applyWith(size, type, speed, v)}>
          <SelectTrigger className="bg-transparent border-[#334155] text-white text-sm" data-testid="cfg-form">
            <SelectValue placeholder="Form Factor" />
          </SelectTrigger>
          <SelectContent className="bg-[#11141D] text-white border-[#334155]">
            {FORM_FACTORS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          onClick={apply}
          disabled={!size && !type && !speed && !form}
          className="bg-[#007AFF] hover:bg-[#005DF0] font-['JetBrains_Mono'] text-sm flex-1"
          data-testid="cfg-apply"
        >
          <MagnifyingGlass size={16} className="mr-1" /> Find Memory
        </Button>
        {(size || type || speed || form) && (
          <Button
            onClick={clear}
            variant="outline"
            className="border-[#334155] font-['JetBrains_Mono'] text-sm"
            data-testid="cfg-clear"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

const InventoryCard = ({ item, onAdd }) => {
  const avail = parseInt(item.quantity_available) || 0;
  const [qtyStr, setQtyStr] = useState('1');
  const qty = Math.max(1, parseInt(qtyStr) || 1);
  const isFixed = item.price_type === 'fixed';
  const setMax = () => setQtyStr(String(avail > 0 ? avail : 1));
  const bumpBy = (delta) => {
    const next = Math.max(1, qty + delta);
    setQtyStr(String(avail > 0 ? Math.min(avail, next) : next));
  };
  return (
    <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 hover:border-[#007AFF] transition-colors">
      <div className="mb-3">
        <p className="font-mono text-cyan-400 font-bold text-sm truncate">{item.part_number}</p>
        <p className="text-xs text-[#94A3B8] line-clamp-2 mt-1">{item.description}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div><span className="text-[#94A3B8]">Avail: </span><span className="text-[#10B981] font-bold">{item.quantity_available}</span></div>
        <div><span className="text-[#94A3B8]">Cond: </span><span className="text-white">{item.condition}</span></div>
      </div>
      <div className="pt-3 border-t border-[#1E293B] flex flex-col gap-2">
        {isFixed ? (
          <p className="text-xl font-bold text-[#10B981] font-mono">${item.unit_price_usd}</p>
        ) : (
          <p className="text-sm font-bold text-[#F59E0B] font-mono">CALL</p>
        )}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => bumpBy(-1)}
            className="h-8 w-8 p-0 text-[#94A3B8] hover:text-white"
            data-testid={`inv-qty-minus-${item.part_number}`}
          >
            <Minus size={14} />
          </Button>
          <Input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min="1"
            max={avail || undefined}
            value={qtyStr}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setQtyStr(e.target.value)}
            onBlur={() => setQtyStr(String(qty))}
            className="w-16 bg-transparent border-[#334155] text-center h-8 text-sm"
            data-testid={`inv-qty-input-${item.part_number}`}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => bumpBy(1)}
            className="h-8 w-8 p-0 text-[#94A3B8] hover:text-white"
            data-testid={`inv-qty-plus-${item.part_number}`}
          >
            <Plus size={14} />
          </Button>
          {avail > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={setMax}
              className="h-8 px-2 text-xs border-[#334155] text-[#10B981] font-['JetBrains_Mono']"
              data-testid={`inv-qty-max-${item.part_number}`}
              title={`Set quantity to max available (${avail})`}
            >
              MAX
            </Button>
          )}
        </div>
        <Button
          onClick={() => { onAdd(qty); setQtyStr('1'); }}
          className={`w-full ${isFixed ? 'bg-[#0047AB] hover:bg-[#005DF0]' : 'bg-[#F59E0B] hover:bg-[#D97706] text-black'} text-sm h-9`}
          data-testid={`add-to-cart-${item.part_number}`}
        >
          {isFixed ? `Add ${qty} to Cart` : `Request Quote for ${qty}`}
        </Button>
      </div>
    </div>
  );
};

export default AgentCommerceContent;